import React, { useState, useEffect, useRef } from 'react';
import { collection, query, where, getDocs, addDoc, deleteDoc, doc, updateDoc, Timestamp } from 'firebase/firestore';
import { db, auth } from '../firebase';
import { useApp } from '../App';
import { requireRole } from '../utils/roles';
import { MediaServer } from '../utils/constants';
import { uploadImageOnly } from '../utils/mediaUpload';

const WorkoutCategories = {
  skating: { label: '스케이팅', icon: '⛸️' },
  passing: { label: '패스', icon: '🏒' },
  shooting: { label: '슈팅', icon: '🎯' },
  stick: { label: '스틱 핸들링', icon: '🥍' },
  conditioning: { label: '체력 훈련', icon: '💪' },
  goalie: { label: '골리 훈련', icon: '🧤' },
  team_play: { label: '팀 플레이', icon: '👥' },
  other: { label: '기타', icon: '📋' },
};

const Difficulties = {
  beginner: { label: '초급', color: '#4ADE80' },
  intermediate: { label: '중급', color: '#FBBF24' },
  advanced: { label: '고급', color: '#F59E0B' },
  expert: { label: '전문가', color: '#EF4444' },
};

// 구버전에 저장된 raw IP URL을 정규 도메인으로 교정 (iOS ATS가 http 차단)
const toWebUrl = (url) => {
  if (!url) return url;
  return url
    .replace('http://122.45.112.83:5000', MediaServer.baseURL)
    .replace('http://121.78.119.123:5000', MediaServer.baseURL);
};

export default function WorkoutPanel() {
  const { currentTeam, isStaff, roles, userProfile } = useApp();
  const canEdit = isStaff;   // 훈련 프로그램 = 운영진 (W1-01)
  const [workouts, setWorkouts] = useState([]);
  const [selected, setSelected] = useState(null);
  const [filterCategory, setFilterCategory] = useState('all');
  const [filterDifficulty, setFilterDifficulty] = useState('all');
  const [showForm, setShowForm] = useState(false);
  const [loading, setLoading] = useState(true);

  const [form, setForm] = useState({
    name: '', category: 'skating', difficulty: 'beginner',
    description: '', estimatedDuration: '15', videoURL: '',
  });
  const [saving, setSaving] = useState(false);

  // 도해 이미지
  const [pendingFiles, setPendingFiles] = useState([]);   // 등록 폼에서 미리 고른 파일
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadLabel, setUploadLabel] = useState('');
  const [lightbox, setLightbox] = useState(null);
  const formFileRef = useRef(null);
  const detailFileRef = useRef(null);

  useEffect(() => { if (currentTeam) loadWorkouts(); }, [currentTeam]); // eslint-disable-line

  const loadWorkouts = async () => {
    setLoading(true);
    try {
      const teamQ = query(collection(db, 'workouts'), where('teamId', '==', currentTeam.id));
      const teamSnap = await getDocs(teamQ);
      const teamList = teamSnap.docs.map(d => ({ id: d.id, ...d.data() }));

      const publicQ = query(collection(db, 'workouts'), where('isPublic', '==', true));
      const publicSnap = await getDocs(publicQ);
      const publicList = publicSnap.docs.map(d => ({ id: d.id, ...d.data() }));

      const allIds = new Set(teamList.map(w => w.id));
      const combined = [...teamList, ...publicList.filter(w => !allIds.has(w.id))];
      setWorkouts(combined);

      // 선택 중이던 항목 동기화
      if (selected) {
        const fresh = combined.find(w => w.id === selected.id);
        if (fresh) setSelected(fresh);
      }
    } catch (err) { console.error(err); }
    setLoading(false);
  };

  // ── 도해 업로드 ─────────────────────────────────────────────────────────
  const uploadDiagrams = async (files) => {
    const list = Array.from(files).filter(f => f.type.startsWith('image/'));
    if (list.length === 0) { alert('이미지 파일만 첨부할 수 있습니다.'); return []; }
    const results = [];
    setUploading(true);
    for (let i = 0; i < list.length; i++) {
      const file = list[i];
      setUploadLabel(`${file.name} (${i + 1}/${list.length})`);
      setUploadProgress(0);
      try {
        const r = await uploadImageOnly(file, currentTeam.id, (p) => setUploadProgress(p));
        results.push({
          url: r.url,
          fileName: r.fileName,
          width: r.width,
          height: r.height,
          caption: '',
          uploadedBy: auth.currentUser?.uid || null,
          uploadedByName: userProfile?.name || null,
          uploadedAt: Timestamp.now(),
        });
      } catch (err) {
        alert(`업로드 실패 (${file.name}): ${err.message}`);
      }
    }
    setUploading(false);
    setUploadProgress(0);
    setUploadLabel('');
    return results;
  };

  const addDiagramsToWorkout = async (workout, files) => {
    if (!requireRole(roles, 'isStaff')) return;
    const uploaded = await uploadDiagrams(files);
    if (uploaded.length === 0) return;
    const next = [...(workout.diagrams || []), ...uploaded];
    try {
      await updateDoc(doc(db, 'workouts', workout.id), { diagrams: next, updatedAt: Timestamp.now() });
      setWorkouts(prev => prev.map(w => w.id === workout.id ? { ...w, diagrams: next } : w));
      setSelected(prev => prev && prev.id === workout.id ? { ...prev, diagrams: next } : prev);
    } catch (err) { alert('저장 실패: ' + err.message); }
  };

  const removeDiagram = async (workout, index) => {
    if (!requireRole(roles, 'isStaff')) return;
    if (!window.confirm('이 도해를 삭제하시겠습니까?')) return;
    const next = (workout.diagrams || []).filter((_, i) => i !== index);
    try {
      await updateDoc(doc(db, 'workouts', workout.id), { diagrams: next, updatedAt: Timestamp.now() });
      setWorkouts(prev => prev.map(w => w.id === workout.id ? { ...w, diagrams: next } : w));
      setSelected(prev => prev && prev.id === workout.id ? { ...prev, diagrams: next } : prev);
    } catch (err) { alert('삭제 실패: ' + err.message); }
  };

  const moveDiagram = async (workout, index, dir) => {
    const arr = [...(workout.diagrams || [])];
    const j = index + dir;
    if (j < 0 || j >= arr.length) return;
    [arr[index], arr[j]] = [arr[j], arr[index]];
    try {
      await updateDoc(doc(db, 'workouts', workout.id), { diagrams: arr, updatedAt: Timestamp.now() });
      setWorkouts(prev => prev.map(w => w.id === workout.id ? { ...w, diagrams: arr } : w));
      setSelected(prev => prev && prev.id === workout.id ? { ...prev, diagrams: arr } : prev);
    } catch (err) { alert('순서 변경 실패: ' + err.message); }
  };

  // ── 워크아웃 등록/삭제 ──────────────────────────────────────────────────
  const handleCreate = async (e) => {
    if (!requireRole(roles, 'isStaff')) return;
    e.preventDefault();
    if (!form.name) return;
    setSaving(true);
    try {
      const diagrams = pendingFiles.length > 0 ? await uploadDiagrams(pendingFiles) : [];
      const docRef = await addDoc(collection(db, 'workouts'), {
        name: form.name,
        category: form.category,
        difficulty: form.difficulty,
        description: form.description,
        estimatedDuration: parseInt(form.estimatedDuration, 10) || 15,
        videoURL: form.videoURL || null,
        diagrams,
        isPublic: false,
        teamId: currentTeam.id,
        createdBy: auth.currentUser.uid,
        createdAt: Timestamp.now(),
      });
      setShowForm(false);
      setForm({ name: '', category: 'skating', difficulty: 'beginner', description: '', estimatedDuration: '15', videoURL: '' });
      setPendingFiles([]);
      await loadWorkouts();
      setSelected({ id: docRef.id, name: form.name, category: form.category, difficulty: form.difficulty, description: form.description, estimatedDuration: parseInt(form.estimatedDuration, 10) || 15, videoURL: form.videoURL || null, diagrams, teamId: currentTeam.id });
    } catch (err) { alert('등록 실패: ' + err.message); }
    setSaving(false);
  };

  const handleDelete = async (id) => {
    if (!requireRole(roles, 'isStaff')) return;
    if (!window.confirm('이 워크아웃을 삭제하시겠습니까?')) return;
    try {
      await deleteDoc(doc(db, 'workouts', id));
      setWorkouts(prev => prev.filter(w => w.id !== id));
      if (selected?.id === id) setSelected(null);
    } catch (err) { alert('삭제 실패'); }
  };

  const filtered = workouts.filter(w => {
    if (filterCategory !== 'all' && w.category !== filterCategory) return false;
    if (filterDifficulty !== 'all' && w.difficulty !== filterDifficulty) return false;
    return true;
  });

  const canEditSelected = selected && canEdit && selected.teamId === currentTeam.id;

  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1.2fr', gap: 24, maxWidth: 1400 }}>
      {/* ── 목록 ── */}
      <div style={s.panel}>
        <div style={{ ...s.ph, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h2 style={s.pt}>🏋️ 훈련 프로그램</h2>
          {canEdit && <button onClick={() => setShowForm(!showForm)} style={s.addBtn}>+ 워크아웃 추가</button>}
        </div>

        <div style={{ padding: '8px 16px', display: 'flex', gap: 8, flexWrap: 'wrap', borderBottom: '1px solid rgba(91,196,216,0.04)' }}>
          <select style={s.select} value={filterCategory} onChange={e => setFilterCategory(e.target.value)}>
            <option value="all">전체 카테고리</option>
            {Object.entries(WorkoutCategories).map(([k, v]) => <option key={k} value={k}>{v.icon} {v.label}</option>)}
          </select>
          <select style={s.select} value={filterDifficulty} onChange={e => setFilterDifficulty(e.target.value)}>
            <option value="all">전체 난이도</option>
            {Object.entries(Difficulties).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
          </select>
          <span style={{ fontSize: 12, color: '#778899', lineHeight: '28px' }}>{filtered.length}개</span>
        </div>

        <div style={s.pb}>
          {showForm && (
            <form onSubmit={handleCreate} style={{ padding: 14, backgroundColor: 'rgba(91,196,216,0.03)', borderRadius: 8, marginBottom: 12 }}>
              <input style={s.input} value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} placeholder="워크아웃 이름 * (예: 파워 스케이팅 드릴 01)" required />
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginTop: 8 }}>
                <select style={s.input} value={form.category} onChange={e => setForm(p => ({ ...p, category: e.target.value }))}>
                  {Object.entries(WorkoutCategories).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                </select>
                <select style={s.input} value={form.difficulty} onChange={e => setForm(p => ({ ...p, difficulty: e.target.value }))}>
                  {Object.entries(Difficulties).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                </select>
              </div>
              <input type="number" style={{ ...s.input, marginTop: 8 }} value={form.estimatedDuration} onChange={e => setForm(p => ({ ...p, estimatedDuration: e.target.value }))} placeholder="소요 시간 (분)" />
              <textarea style={{ ...s.input, marginTop: 8, minHeight: 60, resize: 'vertical' }} value={form.description} onChange={e => setForm(p => ({ ...p, description: e.target.value }))} placeholder="설명 · 코칭 포인트" />
              <input style={{ ...s.input, marginTop: 8 }} value={form.videoURL} onChange={e => setForm(p => ({ ...p, videoURL: e.target.value }))} placeholder="영상 URL (선택)" />

              {/* 도해 첨부 */}
              <div style={{ marginTop: 10 }}>
                <button type="button" onClick={() => formFileRef.current?.click()} style={s.attachBtn}>
                  🖼️ 드릴 도해 첨부
                </button>
                <input ref={formFileRef} type="file" multiple accept="image/*" style={{ display: 'none' }}
                  onChange={e => setPendingFiles(Array.from(e.target.files))} />
                {pendingFiles.length > 0 && (
                  <div style={{ marginTop: 8, display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                    {pendingFiles.map((f, i) => (
                      <span key={i} style={{ fontSize: 11, padding: '3px 8px', borderRadius: 10, backgroundColor: 'rgba(91,196,216,0.1)', color: '#5BC4D8' }}>
                        {f.name}
                      </span>
                    ))}
                    <button type="button" onClick={() => setPendingFiles([])}
                      style={{ fontSize: 11, padding: '3px 8px', borderRadius: 10, border: 'none', backgroundColor: 'rgba(255,255,255,0.05)', color: '#8899AA', cursor: 'pointer' }}>
                      비우기
                    </button>
                  </div>
                )}
              </div>

              {uploading && <UploadBar label={uploadLabel} progress={uploadProgress} />}

              <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
                <button type="submit" disabled={saving || uploading} style={s.submitBtn}>{saving ? '저장 중...' : '등록'}</button>
                <button type="button" onClick={() => { setShowForm(false); setPendingFiles([]); }} style={s.cancelBtn}>취소</button>
              </div>
            </form>
          )}

          {loading ? <div style={s.empty}>로딩 중...</div> :
            filtered.length === 0 ? <div style={s.empty}>워크아웃이 없습니다</div> :
            filtered.map(w => {
              const cat = WorkoutCategories[w.category] || WorkoutCategories.other;
              const diff = Difficulties[w.difficulty];
              const diagrams = w.diagrams || [];
              return (
                <div key={w.id} onClick={() => setSelected(w)} style={{
                  display: 'flex', gap: 10, alignItems: 'center',
                  padding: '10px 12px', marginBottom: 6, borderRadius: 8, cursor: 'pointer',
                  backgroundColor: selected?.id === w.id ? 'rgba(91,196,216,0.08)' : 'rgba(255,255,255,0.02)',
                  borderLeft: selected?.id === w.id ? '3px solid #5BC4D8' : '3px solid transparent',
                }}>
                  {diagrams.length > 0 ? (
                    <img src={toWebUrl(diagrams[0].url)} alt=""
                      style={{ width: 40, height: 52, objectFit: 'cover', borderRadius: 4, backgroundColor: '#0d1e2d', flexShrink: 0 }} />
                  ) : (
                    <div style={{ width: 40, height: 52, borderRadius: 4, backgroundColor: 'rgba(255,255,255,0.03)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, flexShrink: 0 }}>
                      {cat.icon}
                    </div>
                  )}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 6 }}>
                      <span style={{ fontWeight: 600, fontSize: 14, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{w.name}</span>
                      <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexShrink: 0 }}>
                        {diagrams.length > 0 && <span style={{ fontSize: 10, color: '#5BC4D8' }}>🖼️ {diagrams.length}</span>}
                        {diff && <span style={{ fontSize: 10, padding: '2px 8px', borderRadius: 8, border: '1px solid ' + diff.color, color: diff.color, fontWeight: 600 }}>{diff.label}</span>}
                        <span style={{ fontSize: 11, color: '#778899' }}>{w.estimatedDuration}분</span>
                      </div>
                    </div>
                    {w.description && <div style={{ fontSize: 12, color: '#778899', marginTop: 3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{w.description}</div>}
                    {w.isPublic && <span style={{ fontSize: 10, color: '#5BC4D8', marginTop: 2, display: 'inline-block' }}>🌐 공개</span>}
                  </div>
                </div>
              );
            })}
        </div>
      </div>

      {/* ── 상세 ── */}
      <div style={s.panel}>
        <div style={{ ...s.ph, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h2 style={s.pt}>📋 워크아웃 상세</h2>
          {canEditSelected && (
            <>
              <button onClick={() => detailFileRef.current?.click()} disabled={uploading} style={s.addBtn}>
                {uploading ? '업로드 중...' : '🖼️ 도해 추가'}
              </button>
              <input ref={detailFileRef} type="file" multiple accept="image/*" style={{ display: 'none' }}
                onChange={e => { addDiagramsToWorkout(selected, e.target.files); e.target.value = ''; }} />
            </>
          )}
        </div>
        <div style={{ padding: 20, maxHeight: 'calc(100vh - 200px)', overflowY: 'auto' }}>
          {selected ? (
            <>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
                <span style={{ fontSize: 28 }}>{(WorkoutCategories[selected.category] || WorkoutCategories.other).icon}</span>
                <div>
                  <h3 style={{ margin: 0, fontSize: 18, fontWeight: 600, color: '#E8ECF1' }}>{selected.name}</h3>
                  <div style={{ fontSize: 12, color: '#778899' }}>{(WorkoutCategories[selected.category] || WorkoutCategories.other).label}</div>
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 20 }}>
                <InfoBox label="소요 시간" value={selected.estimatedDuration + '분'} />
                <InfoBox label="난이도" value={(Difficulties[selected.difficulty] || {}).label || selected.difficulty} />
              </div>

              {uploading && <UploadBar label={uploadLabel} progress={uploadProgress} />}

              {/* 드릴 도해 */}
              <div style={{ marginBottom: 20 }}>
                <div style={{ fontSize: 12, color: '#667788', fontWeight: 600, marginBottom: 8 }}>
                  드릴 도해 {(selected.diagrams || []).length > 0 ? `(${selected.diagrams.length})` : ''}
                </div>
                {(selected.diagrams || []).length === 0 ? (
                  <div style={{
                    padding: '24px 16px', borderRadius: 8, textAlign: 'center', fontSize: 12, color: '#556677',
                    border: '1px dashed rgba(91,196,216,0.15)',
                  }}>
                    등록된 도해가 없습니다
                    {canEditSelected && <><br /><span style={{ fontSize: 11, color: '#445566' }}>우측 상단 '도해 추가'로 코치가 그린 이미지를 올리세요</span></>}
                  </div>
                ) : (
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: 10 }}>
                    {selected.diagrams.map((d, i) => (
                      <div key={i} style={{ position: 'relative', borderRadius: 8, overflow: 'hidden', border: '1px solid rgba(91,196,216,0.12)' }}>
                        <img src={toWebUrl(d.url)} alt={d.fileName || `도해 ${i + 1}`}
                          onClick={() => setLightbox(toWebUrl(d.url))}
                          style={{ width: '100%', display: 'block', backgroundColor: '#0d1e2d', cursor: 'zoom-in' }} />
                        <div style={{ padding: '4px 6px', fontSize: 10, color: '#8899AA', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <span>{i + 1}</span>
                          {canEditSelected && (
                            <span style={{ display: 'flex', gap: 3 }}>
                              <button onClick={() => moveDiagram(selected, i, -1)} disabled={i === 0} style={s.iconBtn} title="앞으로">←</button>
                              <button onClick={() => moveDiagram(selected, i, 1)} disabled={i === selected.diagrams.length - 1} style={s.iconBtn} title="뒤로">→</button>
                              <button onClick={() => removeDiagram(selected, i)} style={{ ...s.iconBtn, color: '#F87171' }} title="삭제">✕</button>
                            </span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {selected.description && (
                <div style={{ marginBottom: 20 }}>
                  <div style={{ fontSize: 12, color: '#667788', fontWeight: 600, marginBottom: 6 }}>설명 · 코칭 포인트</div>
                  <div style={{ fontSize: 14, lineHeight: 1.7, color: '#AAB4C0', whiteSpace: 'pre-wrap' }}>{selected.description}</div>
                </div>
              )}

              {selected.videoURL && (
                <div style={{ marginBottom: 20 }}>
                  <div style={{ fontSize: 12, color: '#667788', fontWeight: 600, marginBottom: 6 }}>영상</div>
                  <a href={selected.videoURL} target="_blank" rel="noopener noreferrer" style={{ fontSize: 13, color: '#5BC4D8', wordBreak: 'break-all' }}>
                    🎬 {selected.videoURL}
                  </a>
                </div>
              )}

              {canEditSelected && (
                <button onClick={() => handleDelete(selected.id)} style={s.delBtn}>워크아웃 삭제</button>
              )}
            </>
          ) : (
            <div style={s.empty}><div style={{ fontSize: 40, marginBottom: 12, opacity: 0.5 }}>👈</div>워크아웃을 선택하세요</div>
          )}
        </div>
      </div>

      {/* 확대 보기 */}
      {lightbox && (
        <div onClick={() => setLightbox(null)} style={{
          position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.88)', zIndex: 9999,
          display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24, cursor: 'zoom-out',
        }}>
          <img src={lightbox} alt="" style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
          <button onClick={() => setLightbox(null)} style={{
            position: 'absolute', top: 20, right: 24, width: 36, height: 36, borderRadius: '50%',
            border: 'none', backgroundColor: 'rgba(255,255,255,0.12)', color: '#fff', fontSize: 18, cursor: 'pointer',
          }}>✕</button>
        </div>
      )}
    </div>
  );
}

function UploadBar({ label, progress }) {
  return (
    <div style={{ padding: 10, marginBottom: 12, borderRadius: 8, backgroundColor: 'rgba(91,196,216,0.05)' }}>
      <div style={{ fontSize: 11, color: '#AAB4C0', marginBottom: 5, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        📤 {label}
      </div>
      <div style={{ height: 4, backgroundColor: 'rgba(91,196,216,0.1)', borderRadius: 2, overflow: 'hidden' }}>
        <div style={{ height: '100%', width: progress + '%', backgroundColor: '#5BC4D8', borderRadius: 2, transition: 'width 0.3s' }} />
      </div>
    </div>
  );
}

function InfoBox({ label, value }) {
  return (
    <div style={{ padding: 12, borderRadius: 8, backgroundColor: 'rgba(255,255,255,0.02)' }}>
      <div style={{ fontSize: 11, color: '#667788', fontWeight: 600, marginBottom: 4 }}>{label}</div>
      <div style={{ fontSize: 14, fontWeight: 500 }}>{value}</div>
    </div>
  );
}

const s = {
  panel: { backgroundColor: 'rgba(255,255,255,0.02)', border: '1px solid rgba(91,196,216,0.06)', borderRadius: 12, overflow: 'hidden' },
  ph: { padding: '14px 20px', borderBottom: '1px solid rgba(91,196,216,0.06)' },
  pt: { margin: 0, fontSize: 15, fontWeight: 600 },
  pb: { padding: 12, maxHeight: 'calc(100vh - 200px)', overflowY: 'auto' },
  empty: { textAlign: 'center', padding: '60px 20px', color: '#556677', fontSize: 14 },
  addBtn: { padding: '6px 14px', borderRadius: 6, border: '1px solid rgba(91,196,216,0.2)', backgroundColor: 'transparent', color: '#5BC4D8', fontSize: 12, cursor: 'pointer', fontWeight: 600 },
  attachBtn: { padding: '7px 14px', borderRadius: 6, border: '1px dashed rgba(91,196,216,0.3)', backgroundColor: 'transparent', color: '#5BC4D8', fontSize: 12, cursor: 'pointer', fontWeight: 600 },
  select: { padding: '4px 10px', borderRadius: 6, border: '1px solid rgba(91,196,216,0.1)', backgroundColor: 'rgba(10,18,32,0.8)', color: '#E8ECF1', fontSize: 12 },
  input: { width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid rgba(91,196,216,0.1)', fontSize: 14, backgroundColor: 'rgba(10,18,32,0.8)', color: '#E8ECF1', boxSizing: 'border-box' },
  submitBtn: { padding: '8px 20px', borderRadius: 6, border: 'none', backgroundColor: '#5BC4D8', color: '#0A1220', fontSize: 12, fontWeight: 700, cursor: 'pointer' },
  cancelBtn: { padding: '8px 16px', borderRadius: 6, border: '1px solid rgba(91,196,216,0.2)', backgroundColor: 'transparent', color: '#8899AA', fontSize: 12, cursor: 'pointer' },
  delBtn: { padding: '8px 16px', borderRadius: 6, border: '1px solid rgba(248,113,113,0.3)', backgroundColor: 'transparent', color: '#F87171', fontSize: 12, cursor: 'pointer' },
  iconBtn: { padding: '1px 5px', borderRadius: 4, border: 'none', backgroundColor: 'rgba(255,255,255,0.06)', color: '#8899AA', fontSize: 10, cursor: 'pointer' },
};
